// ---------------------------------------------------------------------------
// Live rate card
// ---------------------------------------------------------------------------
//
// Everything in wizardConfig.js is the SHIPPED rate card: the plans, prices,
// add-ons and durations the app falls back to. The real one is editable in
// Manage Company > Plans & Pricing and lives on the server, so this module
// fetches it and writes it INTO those exports.
//
// It mutates the existing objects rather than replacing them because
// `PLANS`, `ADD_ONS` and friends are imported by value all over the app
// (the landing page, both wizards, the Employee wizard). Rebinding the
// export would leave every one of those holding the old object; mutating in
// place moves all of them at once.
//
// Marketing copy is DERIVED, not stored. A plan's `sections` — the bullet
// list on the landing page and in both wizards — is built from the features
// ticked on it, so the public page cannot claim something the plan does not
// actually grant. That is the whole reason feature composition is edited as
// checkboxes rather than as free text.

import { PLAN_CATALOG } from '../../global/Server/endpoints';
import {
  PLANS,
  ADD_ONS,
  ADD_ON_BY_ID,
  DURATIONS,
  DURATION_BY_ID,
  GLOBAL_PLAN_ORDER,
  PUBLIC_PLAN_ORDER,
  PLAN_ROLES,
  ADD_ON_ROLES,
  CATALOG_ALIASES,
  PLAN_FEATURES,
  PLAN_CAPABILITIES,
  CATALOG_SETTINGS,
  COLORS,
} from './wizardConfig';

// The feature list is fixed in code on the server (each entry corresponds to
// real behaviour) and travels with every catalog response. Held here so a
// publish response, which carries only the catalog, can still be applied.
let FEATURES = [];
let FEATURE_BY_ID = {};
let CATEGORIES = [];
let catalogVersion = null;

// Subscribers re-render when the catalog lands. Screens that read the rate
// card at mount would otherwise show the shipped defaults until something
// else happened to re-render them.
const listeners = new Set();

export function subscribeToPlanCatalog(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  listeners.forEach((fn) => {
    try {
      fn(catalogVersion);
    } catch (e) {
      /* a bad subscriber must not stop the others */
    }
  });
}

export function planCatalogVersion() {
  return catalogVersion;
}

export function planCatalogFeatures() {
  return FEATURES;
}

export function planCatalogCategories() {
  return CATEGORIES;
}

export function planCatalogFeatureById(id) {
  return FEATURE_BY_ID[id] || null;
}

/**
 * Fold a catalog payload into wizardConfig's exports.
 *
 * `features` / `categories` are optional — they only arrive with a full
 * catalog fetch, and the ones already held are reused otherwise.
 */
export function applyCatalog(catalog, features, categories) {
  if (!catalog || !Array.isArray(catalog.plans) || catalog.plans.length === 0) {
    return false;
  }
  if (Array.isArray(features) && features.length) {
    FEATURES = features;
    FEATURE_BY_ID = features.reduce((acc, f) => {
      acc[f.id] = f;
      return acc;
    }, {});
  }
  if (Array.isArray(categories) && categories.length) CATEGORIES = categories;
  // Without the feature list there is no way to turn feature ids into roles
  // or bullets, and writing plans with neither would strip every company's
  // entitlements in the Employee wizard. Prices alone are not worth that.
  if (FEATURES.length === 0) return false;

  const plans = catalog.plans
    .slice()
    .sort((a, b) => (a.sortOrder || 99) - (b.sortOrder || 99));

  Object.keys(PLANS).forEach((k) => delete PLANS[k]);
  Object.keys(PLAN_ROLES).forEach((k) => delete PLAN_ROLES[k]);
  Object.keys(PLAN_FEATURES).forEach((k) => delete PLAN_FEATURES[k]);
  Object.keys(PLAN_CAPABILITIES).forEach((k) => delete PLAN_CAPABILITIES[k]);
  Object.keys(CATALOG_ALIASES).forEach((k) => delete CATALOG_ALIASES[k]);

  plans.forEach((p) => {
    const featureIds = (p.features || []).filter((id) => FEATURE_BY_ID[id]);
    PLANS[p.id] = {
      id: p.id,
      name: p.name,
      price: p.price,
      color: p.color || COLORS.blue,
      badge: p.badge || null,
      lineup: p.lineup || 'global',
      active: p.active !== false,
      publicVisible: p.publicVisible !== false,
      bundlesAddOns: p.bundlesAddOns !== false,
      features: featureIds,
      sections: sectionsFor(featureIds, p.color || COLORS.blue, p.id),
    };
    PLAN_FEATURES[p.id] = featureIds;
    PLAN_ROLES[p.id] = rolesFor(featureIds);
    PLAN_CAPABILITIES[p.id] = capabilitiesFor(featureIds);
  });

  // Renames recorded by the catalog, so a company still carrying the old
  // string resolves to the plan it is actually on. Never map a name a plan
  // currently uses — "Premium" has meant two different tiers.
  const currentNames = new Set(plans.map((p) => String(p.name || '').trim()));
  plans.forEach((p) => {
    (p.legacyNames || []).forEach((old) => {
      const key = String(old || '').trim();
      if (key && !currentNames.has(key)) CATALOG_ALIASES[key] = p.name;
    });
  });

  if (Array.isArray(catalog.addOns)) {
    const addOns = catalog.addOns
      .slice()
      .sort((a, b) => (a.sortOrder || 99) - (b.sortOrder || 99));
    ADD_ONS.length = 0;
    Object.keys(ADD_ON_BY_ID).forEach((k) => delete ADD_ON_BY_ID[k]);
    Object.keys(ADD_ON_ROLES).forEach((k) => delete ADD_ON_ROLES[k]);
    addOns.forEach((a) => {
      const featureIds = (a.features || []).filter((id) => FEATURE_BY_ID[id]);
      const row = {
        id: a.id,
        name: a.name,
        price: a.price,
        global: a.global !== false,
        active: a.active !== false,
        features: featureIds,
      };
      ADD_ONS.push(row);
      ADD_ON_BY_ID[a.id] = row;
      ADD_ON_ROLES[a.id] = rolesFor(featureIds);
    });
  }

  // Withdrawn terms stay in the list, flagged. A company already billed on
  // a 6-month cycle has to keep resolving its own term after it stops being
  // sold — dropping it here would re-quote them at the 1-month price. The
  // pickers filter with sellableDurations() instead.
  if (Array.isArray(catalog.durations) && catalog.durations.length) {
    DURATIONS.length = 0;
    Object.keys(DURATION_BY_ID).forEach((k) => delete DURATION_BY_ID[k]);
    catalog.durations.forEach((d) => {
      const row = {
        id: String(d.id),
        label: d.label,
        months: d.months,
        discount: d.discount || 0,
        badge: d.badge || null,
        badgeColor: d.badgeColor || undefined,
        active: d.active !== false,
      };
      DURATIONS.push(row);
      DURATION_BY_ID[row.id] = row;
    });
  }

  if (catalog.settings) {
    if (catalog.settings.trialDays != null) {
      CATALOG_SETTINGS.trialDays = Number(catalog.settings.trialDays);
    }
    if (catalog.settings.trialMinWindowDays != null) {
      CATALOG_SETTINGS.trialMinWindowDays = Number(
        catalog.settings.trialMinWindowDays
      );
    }
  }

  GLOBAL_PLAN_ORDER.length = 0;
  PUBLIC_PLAN_ORDER.length = 0;
  plans.forEach((p) => {
    if ((p.lineup || 'global') !== 'global') return;
    GLOBAL_PLAN_ORDER.push(p.id);
    if (p.active !== false && p.publicVisible !== false) {
      PUBLIC_PLAN_ORDER.push(p.id);
    }
  });

  catalogVersion = catalog.version != null ? catalog.version : catalogVersion;
  notify();
  return true;
}

function rolesFor(featureIds) {
  const out = [];
  featureIds.forEach((id) => {
    ((FEATURE_BY_ID[id] || {}).roles || []).forEach((r) => {
      if (!out.includes(r)) out.push(r);
    });
  });
  return out;
}

function capabilitiesFor(featureIds) {
  const out = [];
  featureIds.forEach((id) => {
    const cap = (FEATURE_BY_ID[id] || {}).capability;
    if (cap && !out.includes(cap)) out.push(cap);
  });
  return out;
}

// How a card presents each category. The default is 'auto': a category the
// plan covers completely collapses to "All Features Included", a partial one
// lists exactly what is in it.
//
// ALWAYS_ITEMIZED overrides that to 'itemize' on every plan. These are the
// short lists a buyer is actually deciding over — Dashboard, Customers,
// Ticket Out; Point Watching, Employee Sessions; NFC Tags for Ticket Out,
// Customer Mobile, Link Accounts — where the shorthand hides the exact thing
// they opened the pricing page to check.
const ALWAYS_ITEMIZED = ['Control Center', 'Operations Monitor', 'Extras'];

// Per-plan exceptions, keyed by plan id then category. 'collapse' forces the
// shorthand on a tier where the spelled-out list would only be long: by the
// time someone is reading Premium, "everything" is the message, and eight
// Control Center rows plus two Operations Monitor rows push the price and
// the buy button off the card.
//
// CAUTION: 'collapse' prints "All Features Included" whether or not the plan
// holds every feature in the category — the one place this module can claim
// more than the plan grants. Premium's Readings is exactly that case: it
// does not include Reading Machine Pictures, which is sold as an add-on.
// Adding a Readings feature Premium lacks makes this row say something
// untrue, so revisit it there, not here.
const SECTION_OVERRIDES = {
  regular: { 'Control Center': 'collapse' },
  premium: {
    'Control Center': 'collapse',
    Readings: 'collapse',
    'Operations Monitor': 'collapse',
  },
  alpha: { 'Control Center': 'collapse', 'Operations Monitor': 'collapse' },
};

/**
 * Turn a plan's feature ids into the section list the marketing cards
 * render, following ALWAYS_ITEMIZED and SECTION_OVERRIDES above and falling
 * back to collapsing a fully-covered category to "All Features Included" —
 * the same shorthand the hand-written config uses.
 */
function sectionsFor(featureIds, color, planId) {
  const owned = new Set(featureIds);
  const overrides = SECTION_OVERRIDES[planId] || {};
  const order = CATEGORIES.length
    ? CATEGORIES
    : FEATURES.reduce((acc, f) => {
        if (!acc.includes(f.category)) acc.push(f.category);
        return acc;
      }, []);

  return order
    .map((category) => {
      const all = FEATURES.filter((f) => f.category === category);
      const mine = all.filter((f) => owned.has(f.id));
      if (mine.length === 0) return null;
      const mode =
        overrides[category] ||
        (ALWAYS_ITEMIZED.includes(category) ? 'itemize' : 'auto');
      // A one-feature category is never collapsed: "All Features Included"
      // is longer than the feature it would be standing in for.
      const collapse =
        all.length > 1 &&
        (mode === 'collapse' ||
          (mode === 'auto' && mine.length === all.length));
      if (collapse) {
        return { title: category, color, note: 'All Features Included' };
      }
      return { title: category, color, items: mine.map((f) => f.label) };
    })
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Fetching
// ---------------------------------------------------------------------------

let inFlight = null;
let loaded = false;

/**
 * Fetch the public rate card once per page load and apply it.
 *
 * Deliberately plain `fetch` rather than AuthUtil: the landing page and the
 * sign-up wizard call this with no session, and the endpoint is permitAll.
 * A failure is silent by design — the shipped defaults are already in place,
 * so the worst case is a stale price, not a broken page.
 */
export function ensurePlanCatalog(force) {
  if (loaded && !force) return Promise.resolve(false);
  if (inFlight) return inFlight;
  inFlight = fetch(PLAN_CATALOG.PUBLIC_CATALOG, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      inFlight = null;
      if (!data || !data.success) return false;
      loaded = true;
      return applyCatalog(data, data.features, data.categories);
    })
    .catch(() => {
      inFlight = null;
      return false;
    });
  return inFlight;
}
